/**
 * MicLevelIndicator (LUM-82)
 *
 * Small in-call "we can hear you" meter. Subscribes to the live level
 * feed itself and repaints at a fixed 4Hz, so ~10Hz capture frames
 * never re-render the interview page. A feed that dries up (dead
 * capture path) decays to the quiet state instead of freezing.
 */

import { Box, Group, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import type { MicLevelListener } from '@/hooks/useOpenAIRealtimeCall';
import { MIC_HEARD_THRESHOLD } from '@/services/mic-level';

export interface MicLevelIndicatorProps {
  subscribe: (listener: MicLevelListener) => () => void;
}

/** Bar n lights at BAR_THRESHOLDS[n] on the shared mic scale. */
const BAR_THRESHOLDS = [MIC_HEARD_THRESHOLD, 12, 24, 48, 96] as const;
const REPAINT_MS = 250;
/** "We can hear you" holds this long after the last voiced frame so
 *  the label does not flicker between words. */
const HEARD_HOLD_MS = 1_500;
/** No frame for this long = no level, whatever the last frame said. */
const FRAME_STALE_MS = 500;

export function MicLevelIndicator({
  subscribe,
}: Readonly<MicLevelIndicatorProps>) {
  const [bars, setBars] = useState(0);
  const [heard, setHeard] = useState(false);
  const latestRef = useRef({ level: 0, at: 0, heardAt: 0 });

  useEffect(
    () =>
      subscribe(level => {
        const now = Date.now();
        const latest = latestRef.current;
        latest.level = level;
        latest.at = now;
        if (level >= MIC_HEARD_THRESHOLD) latest.heardAt = now;
      }),
    [subscribe],
  );

  useEffect(() => {
    const id = window.setInterval(() => {
      const now = Date.now();
      const { level, at, heardAt } = latestRef.current;
      const live = now - at <= FRAME_STALE_MS ? level : 0;
      setBars(BAR_THRESHOLDS.filter(t => live >= t).length);
      setHeard(heardAt > 0 && now - heardAt <= HEARD_HOLD_MS);
    }, REPAINT_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <Group gap={8} align="center" wrap="nowrap">
      <Group
        gap={2}
        align="flex-end"
        wrap="nowrap"
        h={12}
        role="meter"
        aria-label="Microphone level"
        aria-valuemin={0}
        aria-valuemax={BAR_THRESHOLDS.length}
        aria-valuenow={bars}
      >
        {BAR_THRESHOLDS.map((threshold, i) => (
          <Box
            key={threshold}
            w={3}
            h={4 + i * 2}
            style={{
              borderRadius: 1,
              background:
                i < bars
                  ? 'var(--mantine-color-green-6)'
                  : 'rgba(29, 36, 50, 0.18)',
            }}
          />
        ))}
      </Group>
      <Text fz={12} c="navy.6">
        {heard ? 'We can hear you' : 'Your microphone is quiet'}
      </Text>
    </Group>
  );
}
